import type { ComponentType, SVGProps } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircleIcon, ExclamationCircleIcon, ExclamationTriangleIcon, InfoCircleIcon } from '@c2fo/liquidity/icons';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { createKpiSpotlightUrl } from './createKpiSpotlightUrl';
import KpiSpotlightBannerShell from './KpiSpotlightBannerShell';
import type { KpiSpotlightItem, KpiSpotlightKpi } from './useKpiSpotlight';

export interface KpiSpotlightCardProps {
  spotlight: KpiSpotlightItem;
  makerOrganizationUuid: string;
  currency: string;
  buyerName: string;
}

interface KpiVisualConfig {
  // Per-KPI Liquidity theme colors from design (orange/red/lightGreen/lightBlue) — not derived from
  // Alert's `type` tokens, since none of the standard info/warning/error families match these.
  // Written as full literal class names (not template-interpolated) so Tailwind's static scan finds them.
  containerClassName: string;
  accentClassName: string;
  textClassName: string;
  iconClassName: string;
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  titleKey: 'invoiceCentral.kpiSpotlight.insightTitle' | 'invoiceCentral.kpiSpotlight.alertTitle';
  labelKey: string;
  subtextKey: string;
}

const KPI_VISUAL_CONFIG: Record<KpiSpotlightKpi, KpiVisualConfig> = {
  invoices_due_this_week: {
    containerClassName: 'border-lightGreen-500 bg-lightGreen-50',
    accentClassName: 'bg-lightGreen-500',
    textClassName: 'text-lightGreen-900',
    iconClassName: 'fill-lightGreen-500',
    icon: InfoCircleIcon,
    titleKey: 'invoiceCentral.kpiSpotlight.insightTitle',
    labelKey: 'invoiceCentral.kpiSpotlight.invoicesDueThisWeek.label',
    subtextKey: 'invoiceCentral.kpiSpotlight.invoicesDueThisWeek.subtext',
  },
  approved_this_week: {
    containerClassName: 'border-lightBlue-500 bg-lightBlue-50',
    accentClassName: 'bg-lightBlue-500',
    textClassName: 'text-lightBlue-900',
    iconClassName: 'fill-lightBlue-500',
    icon: CheckCircleIcon,
    titleKey: 'invoiceCentral.kpiSpotlight.insightTitle',
    labelKey: 'invoiceCentral.kpiSpotlight.approvedThisWeek.label',
    subtextKey: 'invoiceCentral.kpiSpotlight.approvedThisWeek.subtext',
  },
  deductions_this_week: {
    containerClassName: 'border-orange-500 bg-orange-50',
    accentClassName: 'bg-orange-500',
    textClassName: 'text-orange-900',
    iconClassName: 'fill-orange-500',
    icon: ExclamationTriangleIcon,
    titleKey: 'invoiceCentral.kpiSpotlight.alertTitle',
    labelKey: 'invoiceCentral.kpiSpotlight.deductionsThisWeek.label',
    subtextKey: 'invoiceCentral.kpiSpotlight.deductionsThisWeek.subtext',
  },
  overdue_invoices: {
    containerClassName: 'border-red-500 bg-red-50',
    accentClassName: 'bg-red-500',
    textClassName: 'text-red-900',
    iconClassName: 'fill-red-500',
    icon: ExclamationCircleIcon,
    titleKey: 'invoiceCentral.kpiSpotlight.alertTitle',
    labelKey: 'invoiceCentral.kpiSpotlight.overdueInvoices.label',
    subtextKey: 'invoiceCentral.kpiSpotlight.overdueInvoices.subtext',
  },
};

/**
 * Pure presentational render of one spotlight KPI — no data fetching, so it can be reused for both
 * the real fetched result and hardcoded visual-QA samples.
 */
const KpiSpotlightCard = ({ spotlight, makerOrganizationUuid, currency, buyerName }: KpiSpotlightCardProps) => {
  const { t } = useTranslation();
  const { asCurrencyCompact } = useLocaleFormat();
  const { trackEnterpriseEvent } = useReporting();

  const { kpi, severity, value, ctaDeepLink } = spotlight;
  const spotlightCurrency = spotlight.currency ?? currency;
  const config = KPI_VISUAL_CONFIG[kpi];

  return (
    <KpiSpotlightBannerShell
      containerClassName={config.containerClassName}
      accentClassName={config.accentClassName}
      textClassName={config.textClassName}
      icon={config.icon}
      iconClassName={config.iconClassName}
      title={t(config.titleKey, { buyerName, label: t(config.labelKey) })}
      description={
        <>
          <span className="font-semibold">{asCurrencyCompact(value.amount, spotlightCurrency)}</span>
          <span aria-hidden="true">•</span>
          <span>{t(config.subtextKey, { count: value.count })}</span>
        </>
      }
      action={
        ctaDeepLink
          ? {
              text: t('invoiceCentral.kpiSpotlight.viewFinanceIq'),
              onClick: () => {
                trackEnterpriseEvent('invoice-central::kpi-spotlight::clicked', { kpi, severity });
                window.open(
                  createKpiSpotlightUrl(makerOrganizationUuid, ctaDeepLink, spotlightCurrency),
                  '_blank',
                  'noreferrer'
                );
              },
            }
          : undefined
      }
    />
  );
};

export default KpiSpotlightCard;
